/**
 * VÉRAULT Portal — Advisor · Trip Requests Inbox (Wave 26.13 — VIP)
 *
 * Where past/active clients submit future-trip wishes. Each request can be
 * converted into a draft Experience in one click (reuses the
 * convert-to-experience scaffold).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Inbox, Sparkles, X, Plane, Loader2, Crown, Repeat,
} from "lucide-react";
import {
  api, Surface, Heading, PortalButton, fmt, motion as portalMotion,
} from "../../index";

const { fadeInUp } = portalMotion;

function RequestCard({ req, onConvert, onDismiss }) {
  const [busy, setBusy] = useState(null);
  return (
    <li className="p-4 rounded-md border border-[rgba(0,0,0,0.06)] bg-white" data-testid={`trip-request-${req.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif text-[15px] text-[hsl(var(--foreground))]">
              {req.client_name}
            </span>
            {req.is_repeat_client && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-[0.18em] bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30">
                <Repeat className="size-2.5" strokeWidth={1.5} />
                Repeat client
              </span>
            )}
            {(req.client_completed_trips || 0) >= 3 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-[0.18em] bg-[hsl(var(--accent-gold))]/20 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/40">
                <Crown className="size-2.5" strokeWidth={1.5} />
                VIP
              </span>
            )}
          </div>
          <p className="mt-1.5 font-serif text-[17px] text-[hsl(var(--foreground))]">
            {req.destination_idea}
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            {[req.timeframe, req.occasion, req.travelers_hint, req.budget_band]
              .filter(Boolean).join(" · ") || "No timing details yet"}
          </p>
          {req.notes && (
            <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] max-w-2xl">
              "{req.notes}"
            </p>
          )}
          <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            Submitted {req.created_at ? fmt.formatDate(req.created_at) : "—"}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <PortalButton
            variant="primary"
            size="sm"
            icon={busy === "convert" ? <Loader2 className="size-3.5 animate-spin" /> : <Plane className="size-3.5" />}
            onClick={async () => {
              setBusy("convert");
              try { await onConvert(req); } finally { setBusy(null); }
            }}
            disabled={!!busy}
            data-testid={`request-convert-${req.id}`}
          >
            Convert to Experience
          </PortalButton>
          <PortalButton
            variant="ghost"
            size="sm"
            icon={busy === "dismiss" ? <Loader2 className="size-3.5 animate-spin" /> : <X className="size-3.5" />}
            onClick={async () => {
              if (!window.confirm("Dismiss this request? The client won't be notified.")) return;
              setBusy("dismiss");
              try { await onDismiss(req); } finally { setBusy(null); }
            }}
            disabled={!!busy}
            data-testid={`request-dismiss-${req.id}`}
          >
            Dismiss
          </PortalButton>
        </div>
      </div>
    </li>
  );
}

export default function TripRequestsInbox() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState(null);
  const [status, setStatus] = useState("pending");

  const fetchRequests = async () => {
    try {
      const { data } = await api.get(`/advisor/trip-requests?status=${status}`);
      setRequests(data || []);
    } catch { setRequests([]); }
  };

  useEffect(() => { fetchRequests(); }, [status]);

  const handleConvert = async (req) => {
    try {
      const { data } = await api.post(`/advisor/trip-requests/${req.id}/convert`, {});
      window.dispatchEvent(new CustomEvent("verault:trip-requests-changed"));
      if (data?.trip_id) navigate(`/portal/advisor/journeys/${data.trip_id}?tab=overview`);
    } catch (e) { window.alert(e?.response?.data?.detail || "Couldn't convert this request."); }
  };

  const handleDismiss = async (req) => {
    try {
      await api.post(`/advisor/trip-requests/${req.id}/dismiss`);
      window.dispatchEvent(new CustomEvent("verault:trip-requests-changed"));
      fetchRequests();
    } catch (e) { window.alert(e?.response?.data?.detail || "Couldn't dismiss."); }
  };

  return (
    <motion.div variants={fadeInUp} initial="hidden" animate="visible" className="space-y-5" data-testid="trip-requests-inbox">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <Heading level="h2">Future trip requests</Heading>
          <p className="mt-1 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))] max-w-2xl">
            Wishes from your clients waiting to be turned into something beautiful.
          </p>
        </div>
        <div className="inline-flex p-0.5 rounded-full bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.06)]" data-testid="trip-requests-status-toggle">
          {[
            { id: "pending", label: "Pending" },
            { id: "converted", label: "Converted" },
            { id: "dismissed", label: "Dismissed" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              className={`px-3.5 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                status === s.id
                  ? "bg-white text-[hsl(var(--foreground))] shadow-sm"
                  : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`}
              data-testid={`trip-requests-tab-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <Surface variant="default" padded>
        {requests === null ? (
          <div className="flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            <Loader2 className="size-3.5 animate-spin" /> Listening for wishes…
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <Inbox className="size-6 mx-auto text-[hsl(var(--foreground-muted))]" strokeWidth={1.25} />
            <p className="mt-3 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]">
              {status === "pending"
                ? "No future requests yet. They'll appear here as clients dream."
                : `No ${status} requests.`}
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="trip-requests-list">
            {requests.map((r) => (
              <RequestCard key={r.id} req={r} onConvert={handleConvert} onDismiss={handleDismiss} />
            ))}
          </ul>
        )}
      </Surface>
    </motion.div>
  );
}
